import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import Modal from "../Modal";
import Button from "../Button";
import InputField from "../InputField";
import { updateLoan } from "../../services/loanService";

export interface EditLoanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  loanId: string | number;
  initialData: {
    loan_amount?: string | number;
    interest_rate?: string | number;
    tenure_months?: string | number;
    start_date?: string;
    loan_created_date?: string;
    document_charges?: string | number;
  };
}

export default function EditLoanModal({
  isOpen,
  onClose,
  onSuccess,
  loanId,
  initialData,
}: EditLoanModalProps) {
  const [formData, setFormData] = useState({
    loan_amount: "",
    interest_rate: "",
    tenure_months: "",
    start_date: "",
    loan_created_date: "",
    document_charges: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && initialData) {
      setFormData({
        loan_amount: String(initialData.loan_amount ?? ""),
        interest_rate: String(initialData.interest_rate ?? ""),
        tenure_months: String(initialData.tenure_months ?? ""),
        start_date: initialData.start_date ?? "",
        loan_created_date: initialData.loan_created_date ?? "",
        document_charges: String(initialData.document_charges ?? ""),
      });
    }
  }, [isOpen, initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.loan_amount) {
      toast.error("Loan amount is required");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        id: loanId,
        loan_amount: Number(formData.loan_amount),
        interest_rate: formData.interest_rate
          ? Number(formData.interest_rate)
          : undefined,
        tenure_months: formData.tenure_months
          ? Number(formData.tenure_months)
          : undefined,
        start_date: formData.start_date || undefined,
        loan_created_date: formData.loan_created_date || undefined,
        document_charges: formData.document_charges,
      };

      const res = await updateLoan(payload);

      if (res.success) {
        toast.success("Loan updated successfully");
        onClose();
        onSuccess?.();
      } else {
        toast.error(res.message || "Failed to update loan");
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to update loan");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Loan Details">
      <form onSubmit={handleSubmit} className="space-y-4 ">
        <p className="font-medium "> Loan Amount</p>
        <InputField
          name="loan_amount"
          type="number"
          step="0.01"
          value={formData.loan_amount}
          onChange={handleChange}
          placeholder="Enter loan amount"
        />
        <p className="font-medium "> Interest Rate (%)</p>
        <InputField
          name="interest_rate"
          type="number"
          step="0.01"
          value={formData.interest_rate}
          onChange={handleChange}
          placeholder="Enter interest rate"
        />
        <p className="font-medium "> Tenure (Months)</p>
        <InputField
          name="tenure_months"
          type="number"
          value={formData.tenure_months}
          onChange={handleChange}
          placeholder="Enter tenure in months"
        />
        <p className="font-medium "> Start Date</p>
        <InputField
          name="start_date"
          type="date"
          value={formData.start_date}
          onChange={handleChange}
        />
        <p className="font-medium ">Created Date</p>
        <InputField
          name="loan_created_date"
          type="date"
          value={formData.loan_created_date}
          onChange={handleChange}
        />
        <p className="font-medium ">Document Charges</p>
        <InputField
          name="document_charges"
          type="number"
          step="0.01"
          value={formData.document_charges}
          onChange={handleChange}
          placeholder="Enter document charges"
        />
        <div className="flex gap-3 justify-end pt-4">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            type="button"
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? "Saving..." : "Submit"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
